import { StatusChip } from '@/components'
import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { useLocalStorage } from '@/features/fleet/hooks'
import { sendFcmNotification } from '@/features/notifications/api/fcm.service.ts'
import { RouteDisplayItemList } from '@/features/routing/components/layout/assignment/RouteDisplayItemList'
import useRouteList from '@/features/routing/hooks/useRouteList'
import { useGetAllUsersByRole } from '@/features/user/hooks/useEmployeeUserList.ts'
import { useAllVehiclesForCompany } from '@/features/vehicles/hooks/useVehicleList'
import { Box, Button, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UserRole } from '@/features/user/api'
import { useCompany } from '@/contexts'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar'

export const Route = createFileRoute(
  '/_authenticated/$companySlug/route-planning/assignment'
)({
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['ROUTING_READ', 'ROUTING_WRITE'])
  },
  component: RouteComponent
})

const KEY_ASSIGNEMENTS = 'routes_assignments'

function RouteComponent() {
  const { sites } = useCompany()
  const navigate = Route.useNavigate()
  const { t } = useTranslation('_routes')
  const routeList = useRouteList()
  const itineraryList = useMemo(() => routeList.data ?? [], [routeList.data])
  const { showSuccess } = useMessageSnackbar()

  const vehicleListQuery = useAllVehiclesForCompany(
    sites ? { siteIds: sites.map(site => String(site.id)) } : undefined
  )
  const vehicleListFromDatabase = useMemo(
    () => vehicleListQuery.data?.pages.flatMap(p => p.content) ?? [],
    [vehicleListQuery.data]
  )

  const driverListQuery = useGetAllUsersByRole(
    UserRole.enum.DRIVER,
    sites ? sites.map(site => String(site.id)) : undefined
  )
  const driverListFromDatabase = useMemo(
    () => driverListQuery.data ?? [],
    [driverListQuery.data]
  )
  const numberOfIteneraries = itineraryList.length ?? 0

  const [storedAssignments, setStoredAssignments] = useLocalStorage<
    Record<string, { vehicleName: string; driverName: string }>
  >(KEY_ASSIGNEMENTS, {})

  // memoize derived option lists so they are stable across renders unless
  // the source arrays change
  const vehicleSimplifiedList = useMemo(
    () => vehicleListFromDatabase.map(v => ({ id: v.vin, label: v.name })),
    [vehicleListFromDatabase]
  )

  const driverSimplifiedList = useMemo(
    () =>
      driverListFromDatabase.map(d => ({
        id: d.id,
        label: `${d.firstName} ${d.lastName}`
      })),
    [driverListFromDatabase]
  )

  // this function isn't being used yet
  const handleVehicleAssignment = (itineraryId: string, vehicleName: string) => {
    setStoredAssignments({
      ...storedAssignments,
      [itineraryId]: { ...storedAssignments[itineraryId], vehicleName }
    })
  }

  const handleDriverAssignment = (itineraryId: string, driverName: string) => {
    setStoredAssignments({
      ...storedAssignments,
      [itineraryId]: { ...storedAssignments[itineraryId], driverName }
    })
  }

  const handleApplyAssignments = async () => {
    // TODO persist changes to the backend/database
    const assignedRoutes = Object.entries(storedAssignments).filter(
      ([_, assignment]) => assignment?.driverName
    )

    if (assignedRoutes.length === 0) {
      console.warn('No drivers assigned to any routes')
      return
    }

    // Send a notification to each assigned driver
    const notificationPromises = assignedRoutes.map(async ([_, assignment]) => {
      const driverName = assignment.driverName
      const firstName = driverName.split(' ')[0]

      try {
        await sendFcmNotification({
          topic: `Solutrans`,
          title: `New Route Assignment`,
          body: `Hello ${firstName} ! You have been assigned to a new route`
        })
        console.log(`Notification sent to ${driverName}`)
      } catch (error) {
        console.error(`Failed to send notification to ${driverName}:`, error)
      }
    })

    try {
      await Promise.all(notificationPromises)
      console.log(`Successfully sent ${assignedRoutes.length} notifications`)
      // indicate we navigated here via the Apply Assignments action
      handleMoveToNextStep(true)
    } catch (error) {
      console.error('Failed to send some notifications:', error)
    }
  }

  const handleMoveToNextStep = (withAssignment: boolean = false) => {
    if (withAssignment) {
      showSuccess(t('routes.route-planning.main.applySuccess'))
    }
    navigate({
      to: '/$companySlug/route-planning'
    })
  }

  useEffect(() => {
    if (storedAssignments && Object.keys(storedAssignments).length > 0) {
      return
    }

    setStoredAssignments(
      itineraryList.reduce(
        (assignments, itinerary, idx) =>
          vehicleSimplifiedList[idx]?.label && driverSimplifiedList[idx]?.label
            ? {
                ...assignments,
                [itinerary.id]: {
                  vehicleName: vehicleSimplifiedList[idx].label,
                  driverName: driverSimplifiedList[idx].label
                }
              }
            : assignments,
        {}
      )
    )
  }, [
    storedAssignments,
    itineraryList,
    vehicleSimplifiedList,
    driverSimplifiedList,
    setStoredAssignments
  ])

  return (
    <>
      <PageHeader
        backArrow={{ historyBack: true }}
        subtitle={t('routes.route-planning_assignment.main.abstract')}
      >
        {t('routes.route-planning_assignment.main.mainTitle')}
      </PageHeader>

      <Box sx={{ mt: 3, display: 'flex', width: '100%', alignItems: 'stretch', gap: 2 }}>
        <Typography variant="subtitle5" color="primary">
          {t('routes.route-planning_assignment.main.mainSectionTitle')}
        </Typography>
        {numberOfIteneraries && numberOfIteneraries > 0 && (
          <StatusChip
            variant="highlight"
            label={t('routes.route-planning_assignment.main.newRouteCount', {
              count: numberOfIteneraries
            })}
            sx={{ fontWeight: 500 }}
          />
        )}
      </Box>
      <Box sx={{ height: 16 }} /> {/* Spacer Box */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2 }}
      >
        {itineraryList.map((it, index) => (
          <RouteDisplayItemList
            key={it?.id ?? JSON.stringify(it)}
            itinerary={it}
            vehicleSimplifiedList={vehicleSimplifiedList}
            driverSimplifiedList={driverSimplifiedList}
            index={index}
            vehicleNameFromParent={storedAssignments[it?.id]?.vehicleName ?? ''}
            driverNameFromParent={storedAssignments[it?.id]?.driverName ?? ''}
            onVehicleChange={name => handleVehicleAssignment(it?.id, name)}
            onDriverChange={name => handleDriverAssignment(it?.id, name)}
          />
        ))}
      </Box>
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          px: 2
        }}
      >
        <Button onClick={() => handleMoveToNextStep(false)}>
          <Typography variant="buttonText" color="primary">
            {t('routes.route-planning_assignment.main.discardButtonLabel')}
          </Typography>
        </Button>
        <Button onClick={handleApplyAssignments} variant="contained">
          <Typography variant="buttonText">
            {t('routes.route-planning_assignment.main.applyAssignmentsButtonLabel')}
          </Typography>
        </Button>
      </Box>
    </>
  )
}
